import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Collapse,
  Box,
  Button,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import { ArrowBack, KeyboardArrowRight, KeyboardArrowDown } from "@mui/icons-material";
import SeverityPopoverCell from "./severityCell";
import { ShowActionPopup } from "./showActionPopup";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { ChatbotProvider, useChatbot } from "../searchAgent/chatBotContext";
import ChatBotButton from "../searchAgent/chatbot";
import { useAppTheme } from "core/contexts/theme/theme";
import { fetchAIAnalysisData, fetchAIAnalysisDataStream } from "modules/flywheel/services/incidentService";
import { similarIncidentHeaders } from "modules/flywheel/store/headers";
import { SocAiAgent } from "../newSearchAgent/socaiAgent";
import { SimilarAlertsTableView } from "./searchSimilarNew";
import { SimilarSearchSideModal } from "./similarSearchSideModal";
import StreamMarkdownPage from "./workFlowView";
import {
  setSelectedRow,
  setSummaryModal,
  storeIncidentAnalysis,
} from "modules/flywheel/store/incident";
import SummarySidePanel from "../summarySidebar/summarySidePanel";
import { CustomButtonContainer } from "core/components/global/buttons/button/customButton.styled";
import { ChatBotContainer } from "../incidents.styled";
import { v4 as uuid4 } from "uuid";
import CustomMarkdown from "core/components/global/markdown/markdown";

const AlertTableRow = ({
  row,
  index,
  setIsModalDetails,
}: {
  row: any;
  index: number;
  modeldetails: { type: string; isOpen: boolean };
  setIsModalDetails: (modalDetails: { type: string; isOpen: boolean }) => void;
}) => {
  const [open, setOpen] = useState(true);
  const dispatch = useAppDispatch();
  const { showChatbot } = useChatbot();
  const navigate = useNavigate();

  // Function to handle action clicks
  const onActionClick = (rowId: string, type: string) => {
    console.log("Action clicked onActionClick: ", rowId, type);

    // if (type === "chatbot") {
    //   // showChatbot();

    // } else if (type === "searchSimilar") {
    //   navigate(`/console/cr/flywheel/incidents/search`);

    // } else if (type === "viewIncident") {
    //   dispatch(setSelectedRow({ id: rowId, type: "main" }));
    //   dispatch(setSummaryModal(true));
    // }
  };

  const handleRowClick = () => {
    setOpen(!open); // Just toggle open state without fetching
  };

  // Ensure the sidebar is closed when we switch between findings
  useEffect(() => {
    return () => {
      // Clean up by closing the summary modal when this component unmounts
      dispatch(setSummaryModal(false));
    };
  }, [dispatch]);

  return (
    <>
      <TableRow 
        onClick={handleRowClick} 
        hover 
        sx={{ 
          cursor: 'pointer',
          '&:hover': { bgcolor: theme => theme.palette.action.hover },
          '& > td': { paddingTop: '0.5rem', paddingBottom: '0.5rem' }
        }}
      >
        <TableCell>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {open ? 
              <KeyboardArrowDown fontSize="small" sx={{ mr: 1 }} /> : 
              <KeyboardArrowRight fontSize="small" sx={{ mr: 1 }} />
            }
            {row.findingName}
          </div>
        </TableCell>
        <TableCell>
          <SeverityPopoverCell
            findingName={row.findingName}
            findingId={row.id}
            value={row.severity}
            key={"severity"}
            index={index}
          />
        </TableCell>
        <TableCell>{row.classification}</TableCell>
        <TableCell>{row.org}</TableCell>
        <TableCell>{row.assignedto}</TableCell>
        <TableCell>{row.status}</TableCell>

        <TableCell>
          <div>{row.due}</div>
          <Box mt={0.5}>
            {(() => {
              // Calculate due date progress
              const dueDate = new Date(row.due);
              const today = new Date();
              const createdDate = new Date(row.createdDate);

              const isOverdue = today > dueDate;
              const isClosed = row.status === "Closed";

              // Calculate progress percentage
              let progressPercentage = 0;

              if (isOverdue) {
                // If overdue, fill the bar completely
                progressPercentage = 100;
              } else {
                // Calculate elapsed time as a percentage of total time
                const totalTimeMs = dueDate.getTime() - createdDate.getTime();
                const elapsedTimeMs = today.getTime() - createdDate.getTime();
                progressPercentage = Math.min(
                  100,
                  Math.max(0, Math.floor((elapsedTimeMs / totalTimeMs) * 100))
                );
              }

              // Determine progress bar color
              let barColor = "#4caf50"; // Green - still time
              if (isOverdue) {
                barColor = isClosed ? "#9e9e9e" : "#f44336"; // Grey if closed, red if open
              }

              const barWidth = `${progressPercentage}%`;

              return (
                <Box
                  height={6}
                  width="100px"
                  bgcolor="#eee"
                  borderRadius={4}
                  overflow="hidden"
                >
                  <Box height="100%" width={barWidth} bgcolor={barColor} />
                </Box>
              );
            })()}
          </Box>
        </TableCell>

        {/* <TableCell>
          <ShowActionPopup
            rowId={row.id}
            type={row.type}
            onActionClick={onActionClick}
            options={["Search Agent Suite"]}
          />
        </TableCell> */}
      </TableRow>

      <TableRow sx={{ bgcolor: theme => theme.palette.action.hover }}>
        <TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              margin={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {!row.description ? (
                <Box width="100%" mr={2}>
                  <Skeleton variant="text" width="100%" height={20} />
                  <Skeleton variant="text" width="95%" height={20} />
                  <Skeleton variant="text" width="90%" height={20} />
                </Box>
              ) : (
                <CustomMarkdown content={row.description} showEditButton={false}/>
              )}

              {/* <div style={{ display: "flex", gap: "10px" }}>
                <CustomButtonContainer
                  variant="outlined"
                  onClick={() => {
                    // First close any existing modal
                    dispatch(setSummaryModal(false));

                    // Then set up the new modal
                    setTimeout(() => {
                      setIsModalDetails({ type: "workflow", isOpen: true });
                      dispatch(setSelectedRow({ id: row.id, type: "type" }));
                      dispatch(setSummaryModal(true));
                    }, 50);
                  }}>
                  View Alert
                </CustomButtonContainer>
              </div> */}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const SimilarAlertsTable = () => {
  const [alerts, setAlerts] = useState<any>([]);
  const incidentState = useAppSelector((state) => state.incidents);
  const [loading, setLoading] = useState(true); // Start with loading state
  const [modeldetails, setIsModalDetails] = useState({
    type: "",
    isOpen: false,
  });
  const { apptheme } = useAppTheme();
  const dispatch = useAppDispatch();

  // Store the selected alert's finding name
  const [selectedFindingName, setSelectedFindingName] = useState<string>("");

  // When the component mounts or when we navigate back, close any open sidebars
  useEffect(() => {
    // Close any open sidebar when mounting this component
    dispatch(setSummaryModal(false));

    return () => {
      // Also ensure cleanup on unmount
      dispatch(setSummaryModal(false));
    };
  }, [dispatch]);

  console.log(
    "Incident State incidentState.selectedRow:",
    incidentState.selectedRow
  );

  const textColor = apptheme?.name === "dark-theme" ? "white" : "black";

  // This effect fetches similar alerts when the component mounts - always fetch fresh data
  useEffect(() => {
    // Always fetch alerts whenever this component mounts
    if (
      incidentState.selectedRow !== "" &&
      incidentState.selectedRow !== undefined
    ) {
      // Get the currently selected alert
      const temp: any = incidentState.pageEvents.filter((event) => {
        return event.id === incidentState.selectedRow;
      });

      if (temp.length > 0) {
        const localFindingName = temp[0].findingName;
        // setSelectedFindingName(localFindingName);

        const selectedFindingData = incidentState.pageEvents.filter(
          (event) => event.findingName === localFindingName
        );

        // Set initial alerts without descriptions so we can show the table immediately
        setAlerts(selectedFindingData);

        console.log("Selected Finding Data:", selectedFindingData);

        // Never use cached results, always fetch fresh data
        setAlerts(selectedFindingData);

        // Start fetching all descriptions in parallel
      // ...existing code...

const fetchAllDescriptions = async () => {
  setLoading(true);

  try {
    // For each alert, stream the description and update UI as chunks arrive
    selectedFindingData.forEach((alert) => {
      const alertId = alert.id || "";
      let analysis = "";

      fetchAIAnalysisDataStream(
        JSON.stringify(incidentState.pageEvents.filter(e=>e.id==incidentState.selectedRow)[0])|| "",
        alertId,
        "search_similar_incident",
        (chunk: string) => {
          analysis += chunk;

          // 1. Update global Redux state
          dispatch(storeIncidentAnalysis({
            incidentId: alertId,
            analysis
          }));

          // 2. Update local alerts state for immediate UI feedback
          setAlerts((prevAlerts) => {
            const updatedAlerts = prevAlerts.map((a) =>
              a.id === alertId ? { ...a, description: analysis } : a
            );
            return updatedAlerts;
          });
        }
      );
    });
  } catch (error) {
    console.error("Error fetching descriptions:", error);
  } finally {
    setLoading(false);
  }
};

// ...existing code...

        fetchAllDescriptions();
      } else {
        console.warn(
          "No matching alert found for selected row:",
          incidentState.selectedRow
        );
      }
    }
  }, [incidentState.selectedRow, incidentState.pageEvents]);

  return (
    <div
      
    >
      <ChatbotProvider>
        <ChatBotContainer>
          <Box p={3}>
            <Box
              display="flex"
              alignItems="center"
              mb={2}
              
            >
              <Typography
                variant="h5"
                fontWeight="bold"
                mr={2}
                sx={{ color: textColor }}
              >
                <ArrowBack
                  onClick={() => window.history.back()}
                  style={{ cursor: "pointer" }}
                />
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                mr={2}
                sx={{ color: textColor }}
              >
                Search Similar Alerts
              </Typography>
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    {similarIncidentHeaders.map((header, index) => (
                      <TableCell key={`header-${index}`}>{header}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody className="custom-table-body">
                  {alerts.length === 0 && loading
                    ? // Only show full skeleton rows when we have no data at all
                      [...Array(3)].map((_, index) => (
                        <React.Fragment key={`skeleton-${index}`}>
                          <TableRow>
                            {[...Array(7)].map((_, cellIndex) => (
                              <TableCell key={`skeleton-cell-${cellIndex}`}>
                                <Skeleton
                                  variant="text"
                                  width={cellIndex === 0 ? 150 : 80}
                                  height={24}
                                />
                              </TableCell>
                            ))}
                          </TableRow>
                        </React.Fragment>
                      ))
                    : alerts.map((row, index) => (
                        <AlertTableRow
                          key={row.id}
                          row={row}
                          index={index}
                          modeldetails={modeldetails}
                          setIsModalDetails={setIsModalDetails}
                        />
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <SocAiAgent />
          <ChatBotButton />
        </ChatBotContainer>
      </ChatbotProvider>
    </div>
  );
};

export default SimilarAlertsTable;
